import { Link } from "react-router-dom";
import {
  FileText, FileCheck2, Mail, ClipboardCheck, Stamp, ScrollText,
  ArrowRight, Download, Eye, Sparkles, ShieldCheck, CheckCircle2,
} from "lucide-react";

/**
 * SamplePacketPage — public marketing page showing exactly what visitors
 * receive as their finished USCIS filing packet. No signup required — just
 * click "See the sample PDF" and preview the whole thing.
 *
 * Route: /sample-packet
 */

const PACKET_ITEMS = [
  {
    icon: ClipboardCheck,
    title: "Filing Checklist",
    body: "One page listing every enclosure USCIS expects to see, in the exact order they open the envelope. If anything is missing, the checkbox is red.",
  },
  {
    icon: Mail,
    title: "Cover Letter",
    body: "A two-page letter addressed to the correct USCIS lockbox — auto-selected by the petitioner's state. Lists every enclosure. Signed by the petitioner.",
  },
  {
    icon: FileText,
    title: "Form I-130 (auto-filled)",
    body: "Every field pre-populated from the documents you uploaded. You review, fix anything, and e-sign — no retyping. Ready to print or e-file.",
  },
  {
    icon: FileText,
    title: "Form I-130A (auto-filled)",
    body: "Beneficiary's biographic supplement. 5-year address & employment history pulled directly from what you already entered.",
  },
  {
    icon: Stamp,
    title: "Exhibit tabs & separators",
    body: "Every document you upload becomes a numbered exhibit (EX-01, EX-02…) with a tab separator, so USCIS reviewers see an organized packet.",
  },
  {
    icon: ScrollText,
    title: "Evidence Exhibit List",
    body: "One-page index of every piece of evidence — birth certificates, translations, marriage cert, joint lease, photos. Cross-referenced to exhibit numbers.",
  },
  {
    icon: FileCheck2,
    title: "Filing fee reference sheet",
    body: "Fee amount ($675 for I-130 in 2026), payee (\"U.S. Department of Homeland Security\"), and the current lockbox mailing address printed on a single page.",
  },
];

export default function SamplePacketPage() {
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/sample-packet.pdf`;
  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }} data-testid="sample-packet-page">
      {/* Top bar */}
      <header
        className="border-b sticky top-0 z-10"
        style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      >
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="sample-home-link">
            <div className="font-display font-extrabold text-lg" style={{ color: "var(--text-primary)" }}>
              DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline" style={{ color: "var(--text-secondary)" }}>Home</Link>
            <Link to="/faq" style={{ color: "var(--text-secondary)" }}>FAQ</Link>
            <Link to="/pricing" style={{ color: "var(--text-secondary)" }}>Pricing</Link>
            <Link
              to="/register"
              className="btn-primary text-sm px-4 py-2"
              data-testid="sample-get-started-btn"
            >
              Get started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 sm:py-20" style={{ background: "var(--bg-app)" }}>
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="pill pill-ai mb-4 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> The finished packet
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl font-extrabold mb-4"
            style={{ color: "var(--text-primary)" }}
          >
            See exactly what you'll mail to&nbsp;USCIS — before you pay a penny.
          </h1>
          <p
            className="text-[16px] leading-relaxed max-w-2xl mx-auto mb-8"
            style={{ color: "var(--text-secondary)" }}
          >
            No blurred screenshots. No sales-page hand-waving. Click the button
            below to open a redacted example of the exact packet
            DS-260USA.com assembles for you — signed forms, cover letter,
            checklist, evidence tabs, mailing address, and all.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3"
              data-testid="sample-packet-view-btn"
            >
              <Eye className="w-4 h-4" /> See the sample PDF
            </a>
            <a
              href={pdfUrl}
              download="sample-filing-packet.pdf"
              className="btn-ghost inline-flex items-center gap-2 text-base px-6 py-3"
              style={{ border: "1px solid var(--border-stone)", background: "#FFFFFF" }}
              data-testid="sample-packet-download-btn"
            >
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
          <p
            className="mt-4 text-xs"
            style={{ color: "var(--text-secondary)" }}
          >
            The sample uses fake names (Alex Martinez petitioning for Marta
            Santos). Your real packet is filled with your family's data.
          </p>
        </div>
      </section>

      {/* What's in the packet grid */}
      <section className="pb-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2
              className="font-display text-2xl sm:text-3xl font-extrabold mb-2"
              style={{ color: "var(--text-primary)" }}
            >
              What's inside your finished packet
            </h2>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Seven components, assembled in the exact order USCIS expects.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {PACKET_ITEMS.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.title}
                  className="card-surface p-5 flex items-start gap-3"
                  style={{ background: "#FFFFFF" }}
                  data-testid={`sample-packet-item-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div
                    className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div
                      className="font-display font-bold text-[15px] mb-1"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {it.title}
                    </div>
                    <div className="text-[13.5px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                      {it.body}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section
        className="py-14 border-t"
        style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      >
        <div className="mx-auto max-w-4xl px-6">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <div>
              <div className="w-12 h-12 rounded-md mx-auto mb-3 flex items-center justify-center"
                   style={{ background: "#DCFCE7" }}>
                <CheckCircle2 className="w-6 h-6" style={{ color: "#166534" }} />
              </div>
              <div className="font-display font-bold text-base mb-1" style={{ color: "var(--text-primary)" }}>
                Nothing filed without your OK
              </div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                Every field appears next to the source document. You review,
                edit, and sign before anything is sent.
              </div>
            </div>
            <div>
              <div className="w-12 h-12 rounded-md mx-auto mb-3 flex items-center justify-center"
                   style={{ background: "#FEF3C7" }}>
                <ShieldCheck className="w-6 h-6" style={{ color: "#78350F" }} />
              </div>
              <div className="font-display font-bold text-base mb-1" style={{ color: "var(--text-primary)" }}>
                Optional attorney review
              </div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                Invite a licensed U.S. immigration attorney to read-only-review
                your packet before you mail it — pay only if you want the review.
              </div>
            </div>
            <div>
              <div className="w-12 h-12 rounded-md mx-auto mb-3 flex items-center justify-center"
                   style={{ background: "#EFF6FF" }}>
                <FileCheck2 className="w-6 h-6" style={{ color: "#1E40AF" }} />
              </div>
              <div className="font-display font-bold text-base mb-1" style={{ color: "var(--text-primary)" }}>
                Current USCIS editions
              </div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                We track the latest I-130 / I-130A form editions and lockbox
                addresses automatically — your packet always uses the current
                versions.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 border-t"
        style={{ background: "var(--bg-app)", borderColor: "var(--border-stone)" }}
      >
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2
            className="font-display text-3xl sm:text-4xl font-extrabold mb-3"
            style={{ color: "var(--text-primary)" }}
          >
            Ready to start yours?
          </h2>
          <p
            className="text-[15px] leading-relaxed max-w-2xl mx-auto mb-6"
            style={{ color: "var(--text-secondary)" }}
          >
            Free to try — no card required until you're ready to download the
            filled packet. Progress auto-saves so you can work across multiple
            sessions.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/register"
              className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3"
              data-testid="sample-packet-cta-register"
            >
              Start your case <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/faq"
              className="btn-ghost inline-flex items-center gap-2 text-base px-6 py-3"
              style={{ color: "var(--action)" }}
            >
              Read the FAQ
            </Link>
          </div>
        </div>
      </section>

      {/* Sub-footer */}
      <footer
        className="border-t py-8"
        style={{ borderColor: "var(--border-stone)", background: "#FFFFFF" }}
      >
        <div
          className="mx-auto max-w-6xl px-6 flex flex-wrap gap-4 justify-between items-center text-xs"
          style={{ color: "var(--text-secondary)" }}
        >
          <div>© {new Date().getFullYear()} DS-260USA.com — Sample packet</div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/" className="hover:opacity-80">Home</Link>
            <Link to="/faq" className="hover:opacity-80">FAQ</Link>
            <Link to="/pricing" className="hover:opacity-80">Pricing</Link>
            <Link to="/legal/terms" className="hover:opacity-80">Terms</Link>
            <Link to="/legal/privacy" className="hover:opacity-80">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
